<template>
  <sp-autocomplete
    v-preserve-properties:items="physicians"
    placeholder="Start typing..."
    return-object
    no-filter
    no-data-text="No physicians found"
    item-value="id"
    :loading="isLoadingPhysicians"
    @input="onPhysicianSelect"
    @update-search="onPhysicianUpdateSearch"
  ></sp-autocomplete>
</template>

<script setup>
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { useDebounceFn } from "@vueuse/core";
import { ref } from "vue";
import { createPhysicianApi } from "#/decision_tree_viewer/api/physician";

const { search } = createPhysicianApi();

const emit = defineEmits(["update-selected"]);

const physicians = ref([]);
const isLoadingPhysicians = ref(false);

const selectedPhysician = ref(null);

// Only the newest search may write the results. The generation advances in the listener rather
// than in the debounced work, so a response cannot be accepted once a newer query is on screen.
let latestRequest = 0;

function onPhysicianUpdateSearch({ detail }) {
  const [q] = detail;
  const request = ++latestRequest;

  if (!q) {
    physicians.value = [];
    isLoadingPhysicians.value = false;
    return;
  }

  // If the user has already selected a physician, don't search again
  if (selectedPhysician.value && selectedPhysician.value.title === q) {
    isLoadingPhysicians.value = false;
    return;
  }

  debouncedSearch(request, q);
}

// Debounce the search itself so we don't overwhelm the API. `isLoadingPhysicians` belongs to the
// newest request: the listener's two early returns settle it, and the work below settles it while
// it is still the newest, so a request the picker walked away from never leaves it on.
const debouncedSearch = useDebounceFn(async (request, q) => {
  if (request !== latestRequest) {
    return;
  }

  try {
    isLoadingPhysicians.value = true;

    const results = await search(q);

    if (request === latestRequest) {
      physicians.value = results;
    }
  } catch (error) {
    console.error(error);
  } finally {
    if (request === latestRequest) {
      isLoadingPhysicians.value = false;
    }
  }
});

function onPhysicianSelect({ detail }) {
  const [physician] = detail;
  if (!physician) {
    return;
  }
  selectedPhysician.value = physician;
  emit("update-selected", physician.id);
}
</script>
